/*
 * A Focusable Section can specify a behaviour before focusing an element.
 * I.e. selecting a default element, the first element or an active one.
 *
 * Props:
 *   defaultElement: (default: { down: '' })
 *     The default element that will be focused when entering this section,
 *     depending on the direction it entered.
 *     This can be:
 *       * a valid selector string for "querySelectorAll".
 *       * a NodeList or an array containing DOM elements.
 *       * a single DOM element.
 *       * an empty string.
 *
 *   enterTo: (default: 'default-element')
 *     If the focus comes from another section, you can define which element in this section should be focused first.
 *     This can be:
 *       * 'last-focused' indicates the last focused element before we left this section last time. If this section has never been focused yet, the default element (if any) will be chosen next.
 *       * 'default-element' indicates the element defined in defaultElement.
 *       * an empty string.
 */
import { NavigationFailedEvent } from "./Focusable.js";
import JsSpatialNavigation from "./lib/spatial_navigation.js";
import { useSpatialNavigation } from "./SpatialNavigation";
import MD5 from "crypto-js/md5";
import { logE, logD } from "@blacknut/logging/dist";
import {
  createContext,
  CSSProperties,
  HTMLAttributes,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface LeaveFor {
  left?: (() => boolean) | string;
  right?: (() => boolean) | string;
  up?: (() => boolean) | string;
  down?: (() => boolean) | string;
}

export const LeaveForNowhere: LeaveFor = {
  up: "",
  down: "",
  left: "",
  right: "",
};

export declare type Config = {
  straightOnly?: boolean;
  restrict?: "self-first" | "self-only" | "none";
  distanceMode?: boolean;
};

export interface FocusableSectionProps {
  focusKey: string;
  component?: string;
  defaultElement?:
    | string
    | {
        down?: "first" | "active" | string;
        up?: "first" | "active" | string;
        right?: "first" | "active" | string;
        left?: "first" | "active" | string;
      };
  enterTo?: "" | "last-focused" | "default-element";
  navigableFilter?: (
    e: Element,
    sectionId: string,
    direction: "up" | "down" | "left" | "right",
  ) => boolean;
  className?: string;
  leaveFor?: LeaveFor;
  style?: CSSProperties;
  config?: Config;
  onNavigationFailed?: (e: NavigationFailedEvent) => void;
  disabled?: boolean;
  focused?: boolean;
}

export const getSectionIdFromPath = (path: string) => {
  const res = `s-${MD5(path)}`;
  // console.log("getSectionIdFromPath ", path, res);
  return res;
};

export const FocusPathContext = createContext<{
  focusKey: string;
  sectionId: string;
  onNavigationFailed?: (e: NavigationFailedEvent) => void;
}>({ focusKey: "", sectionId: "" });

export const useFocusPathContext = () => useContext(FocusPathContext);

const FocusableSection = (
  props: PropsWithChildren<FocusableSectionProps & HTMLAttributes<HTMLDivElement>>,
) => {
  const {
    component,
    focusKey,
    children,
    config,
    onNavigationFailed,
    leaveFor,
    defaultElement,
    disabled,
    navigableFilter,
    focused,
  } = props;

  const Component = (component || "div") as any;
  const { focusKey: _focusKey, onNavigationFailed: onNavigationFailedParent } =
    useContext(FocusPathContext);
  const { config: globalConfig } = useSpatialNavigation();
  const ref = useRef<HTMLElement>(null);
  const finalFocusKey = `${_focusKey}/${focusKey}`;
  const sectionId = getSectionIdFromPath(finalFocusKey);
  useEffect(() => {
    if (disabled) {
      //            console.log("Disable ", sectionId);
      JsSpatialNavigation.disable(sectionId);
    } else {
      // console.log("Enable ", sectionId);
      JsSpatialNavigation.enable(sectionId);
    }
  }, [disabled, sectionId]);
  const _onNavigationFailed = useMemo(() => {
    if (!onNavigationFailed && !onNavigationFailedParent) {
      return undefined;
    }
    return (e: NavigationFailedEvent) => {
      if (onNavigationFailed) {
        onNavigationFailed({
          detail: e.detail,
          target: ref.current || e.target,
        });
      }
      if (onNavigationFailedParent) {
        onNavigationFailedParent({
          detail: e.detail,
          target: ref.current || e.target,
        });
      }
    };
  }, [onNavigationFailed, onNavigationFailedParent]);

  useEffect(() => {
    // console.log("Mounting ", finalFocusKey, leaveFor);

    let _defaultElement = defaultElement;
    if (typeof defaultElement === "object") {
      const down =
        defaultElement && defaultElement.down
          ? `.${sectionId}${defaultElement.down}`
          : undefined;
      const up =
        defaultElement && defaultElement.up
          ? `.${sectionId}${defaultElement.up}`
          : undefined;
      const right =
        defaultElement && defaultElement.right
          ? `.${sectionId}${defaultElement.right}`
          : undefined;
      const left =
        defaultElement && defaultElement.left
          ? `.${sectionId}${defaultElement.left}`
          : undefined;
      _defaultElement = { down, up, right, left };
    }

    const sectionConfig = {
      ...globalConfig,
      selector: `.${sectionId}`,
      straightOnly: config?.straightOnly,
      restrict: config?.restrict,
      distanceMode: config?.distanceMode,
      leaveFor,
      navigableFilter,
      defaultElement: _defaultElement,
    };
    const resSectionId = JsSpatialNavigation.add(sectionId, sectionConfig);
    if (focused) {
      JsSpatialNavigation.focus(resSectionId, true);
    }
    return () => {
      // console.log("Unmounting ", finalFocusKey, leaveFor);
      JsSpatialNavigation.remove(sectionId);
    };
  }, [
    config,
    globalConfig,
    leaveFor,
    sectionId,
    defaultElement,
    finalFocusKey,
    navigableFilter,
    focused,
  ]);
  const remainingProps: Partial<FocusableSectionProps> = { ...props };
  delete remainingProps.focusKey;
  delete remainingProps.component;
  delete remainingProps.defaultElement;
  delete remainingProps.enterTo;
  delete remainingProps.navigableFilter;
  delete remainingProps.leaveFor;
  delete remainingProps.config;
  delete remainingProps.onNavigationFailed;
  delete remainingProps.disabled;
  delete remainingProps.focused;

  return (
    <FocusPathContext.Provider
      value={{
        focusKey: `${_focusKey}/${focusKey}`,
        sectionId,
        onNavigationFailed: _onNavigationFailed,
      }}
    >
      <Component {...remainingProps} ref={ref}>
        {children}
      </Component>
    </FocusPathContext.Provider>
  );
};

export default FocusableSection;
const TAG = "SpatialNav";

export const focusSectionWithPath = (
  path: string,
  opts?: { preventScroll: boolean },
) => {
  const id = getSectionIdFromPath(path);
  if (!id) {
    logE(TAG, "Unable to find id for path ", path);
    return;
  }
  logD(TAG, `Force focus to ${id} Focuspath ${path}`);
  JsSpatialNavigation.focus(`@${id}`, false, opts);
};

export const disableAllSectionsExcept = (path: string) => {
  const id = getSectionIdFromPath(path);
  if (!id) {
    logE(TAG, "Unable to find id for path ", path);
    return [];
  }
  return JsSpatialNavigation.disableAllSectionsExcept(id) as string[];
};

export const bachEnableSections = (disabled: string[]) => {
  JsSpatialNavigation.batchEnableSections(disabled);
};

export const focusElementWithSectionPath = (path: string, selector: string) => {
  const id = getSectionIdFromPath(path);
  if (!id) {
    logE(TAG, "Unable to find id for path ", path);
    return;
  }
  logD(TAG, `Force focus to .${id}${selector}`);
  JsSpatialNavigation.focus(`.${id}${selector}`);
};
